/**
 * Admin — Platform Enhancements settings.
 * Business-logic knobs (CO2, gift card limits, quote thresholds, health-score weights)
 * that admins can tune without a redeploy. Also lists challenges + create new.
 *
 * Backend:
 *   GET  /api/settings/platform
 *   POST /api/settings/platform  {key, value}
 *   POST /api/admin/challenges
 *   GET  /api/challenges  (active list)
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Sliders, Save, Trophy, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const SETTING_GROUPS = [
  {
    title: "ESG / CO₂",
    keys: [
      { key: "co2_kg_per_repair", label: "kg CO₂ saved per generic repair", step: 1 },
      { key: "co2_kg_per_battery_replace", label: "kg CO₂ saved per battery replace", step: 1 },
      { key: "co2_kg_per_screen_repair", label: "kg CO₂ saved per screen repair", step: 1 },
    ],
  },
  {
    title: "Gift cards",
    keys: [
      { key: "gift_card_min_gbp", label: "Min amount (£)", step: 1 },
      { key: "gift_card_max_gbp", label: "Max amount (£)", step: 1 },
      { key: "gift_card_expiry_months", label: "Expiry (months)", step: 1 },
    ],
  },
  {
    title: "Cost predictor",
    keys: [
      { key: "cost_predictor_confidence_high", label: "High-confidence threshold", step: 0.05 },
      { key: "cost_predictor_confidence_medium", label: "Medium-confidence threshold", step: 0.05 },
    ],
  },
  {
    title: "Health score weightings (must sum to 100)",
    keys: [
      { key: "hs_weight_battery", label: "Battery weight", step: 1 },
      { key: "hs_weight_screen", label: "Screen weight", step: 1 },
      { key: "hs_weight_repair_history", label: "Repair history weight", step: 1 },
      { key: "hs_weight_software", label: "Software weight", step: 1 },
      { key: "hs_weight_storage", label: "Storage weight", step: 1 },
      { key: "hs_weight_freshness", label: "Freshness weight", step: 1 },
    ],
  },
];

function isoDaysFromNow(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

export default function AdminEnhancements() {
  const [settings, setSettings] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [showNewChallenge, setShowNewChallenge] = useState(false);
  const [newCh, setNewCh] = useState({
    slug: "", name: "", description: "",
    action_type: "complete_repairs", action_target: 3,
    reward_gems: 100, reward_badge: "",
    duration_days: 30,
  });

  const load = async () => {
    try {
      const [s, c] = await Promise.all([
        api.get("/settings/platform"),
        api.get("/challenges"),
      ]);
      setSettings(s.data);
      setDrafts(s.data);
      setChallenges(c.data.challenges || []);
    } catch (e) {
      toast.error("Failed to load settings");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    const value = parseFloat(drafts[key]);
    if (Number.isNaN(value)) return toast.error("Enter a valid number");
    setSaving(key);
    try {
      await api.post("/settings/platform", { key, value });
      toast.success(`${key} saved: ${value}`);
      setSettings(s => ({ ...s, [key]: value }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(null);
  };

  const createChallenge = async () => {
    if (!newCh.slug || !newCh.name) return toast.error("Slug and name required");
    try {
      const payload = {
        slug: newCh.slug,
        name: newCh.name,
        description: newCh.description,
        action_type: newCh.action_type,
        action_target: Number(newCh.action_target),
        reward_gems: Number(newCh.reward_gems),
        reward_badge: newCh.reward_badge || null,
        starts_at: new Date().toISOString(),
        ends_at: isoDaysFromNow(Number(newCh.duration_days) || 30),
      };
      await api.post("/admin/challenges", payload);
      toast.success(`Challenge "${newCh.name}" created`);
      setShowNewChallenge(false);
      setNewCh({ slug: "", name: "", description: "", action_type: "complete_repairs",
                  action_target: 3, reward_gems: 100, reward_badge: "", duration_days: 30 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  if (!settings) return <div className="p-8 text-center text-muted-foreground">Loading platform settings…</div>;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="admin-enhancements-page">
      <div>
        <div className="overline text-secondary flex items-center gap-2"><Sliders className="w-3 h-3" /> Admin</div>
        <h1 className="text-3xl font-display font-light mt-1">Platform Enhancements</h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          Tune the business-logic figures that power CO₂ impact, gift cards, cost predictor and health score —
          without a redeploy. Each field saves independently.
        </p>
      </div>

      <Tabs defaultValue="settings">
        <TabsList>
          <TabsTrigger value="settings" data-testid="tab-settings"><Sliders className="w-4 h-4 mr-1.5" /> Settings</TabsTrigger>
          <TabsTrigger value="challenges" data-testid="tab-challenges"><Trophy className="w-4 h-4 mr-1.5" /> Challenges</TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reload</Button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {SETTING_GROUPS.map(group => (
              <Card key={group.title}>
                <CardHeader><CardTitle className="text-base">{group.title}</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {group.keys.map(({ key, label, step }) => (
                    <div key={key} className="flex items-end gap-2" data-testid={`setting-row-${key}`}>
                      <div className="flex-1">
                        <Label className="text-xs text-muted-foreground">{label}</Label>
                        <Input
                          type="number" step={step}
                          value={drafts[key] ?? ""}
                          onChange={(e) => setDrafts(d => ({ ...d, [key]: e.target.value }))}
                          data-testid={`setting-input-${key}`}
                        />
                      </div>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => save(key)}
                        disabled={saving === key || String(drafts[key]) === String(settings[key])}
                        data-testid={`setting-save-${key}`}
                      >
                        <Save className="w-3.5 h-3.5 mr-1.5" /> {saving === key ? "…" : "Save"}
                      </Button>
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-[11px] text-muted-foreground mt-3">
            Last saved: {settings.updated_at ? new Date(settings.updated_at).toLocaleString("en-GB") : "—"}
          </div>
        </TabsContent>

        <TabsContent value="challenges">
          <div className="flex justify-between items-center mb-3">
            <div className="text-sm text-muted-foreground">{challenges.length} active challenge{challenges.length === 1 ? "" : "s"}</div>
            <Dialog open={showNewChallenge} onOpenChange={setShowNewChallenge}>
              <DialogTrigger asChild>
                <Button size="sm" data-testid="new-challenge-btn"><Plus className="w-4 h-4 mr-1.5" /> New challenge</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Create challenge</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Slug (a-z, 0-9, dashes)</Label>
                    <Input value={newCh.slug} onChange={(e) => setNewCh(c => ({ ...c, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
                           placeholder="repair-hero-jan" data-testid="ch-slug" />
                  </div>
                  <div>
                    <Label>Name</Label>
                    <Input value={newCh.name} onChange={(e) => setNewCh(c => ({ ...c, name: e.target.value }))} data-testid="ch-name" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea rows={2} value={newCh.description} onChange={(e) => setNewCh(c => ({ ...c, description: e.target.value }))} data-testid="ch-desc" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Action type</Label>
                      <Input value={newCh.action_type} onChange={(e) => setNewCh(c => ({ ...c, action_type: e.target.value }))} data-testid="ch-action" />
                    </div>
                    <div>
                      <Label>Target</Label>
                      <Input type="number" min={1} value={newCh.action_target} onChange={(e) => setNewCh(c => ({ ...c, action_target: e.target.value }))} data-testid="ch-target" />
                    </div>
                    <div>
                      <Label>Reward Gems</Label>
                      <Input type="number" min={0} value={newCh.reward_gems} onChange={(e) => setNewCh(c => ({ ...c, reward_gems: e.target.value }))} data-testid="ch-gems" />
                    </div>
                    <div>
                      <Label>Duration (days)</Label>
                      <Input type="number" min={1} value={newCh.duration_days} onChange={(e) => setNewCh(c => ({ ...c, duration_days: e.target.value }))} data-testid="ch-duration" />
                    </div>
                  </div>
                  <div>
                    <Label>Badge (optional slug)</Label>
                    <Input value={newCh.reward_badge} onChange={(e) => setNewCh(c => ({ ...c, reward_badge: e.target.value }))} data-testid="ch-badge" />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={createChallenge} data-testid="ch-create-btn">Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {challenges.length === 0 ? (
            <Card><CardContent className="text-center py-12 text-sm text-muted-foreground">No active challenges. Create one to get started.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {challenges.map(ch => (
                <Card key={ch.id}>
                  <CardContent className="pt-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-medium">{ch.name} <span className="text-xs text-muted-foreground">({ch.slug})</span></div>
                        <div className="text-sm text-muted-foreground">{ch.description}</div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge>+{ch.reward_gems} Gems</Badge>
                        {ch.reward_badge && <Badge variant="outline">🏅 {ch.reward_badge}</Badge>}
                      </div>
                    </div>
                    <div className="mt-2 text-[11px] text-muted-foreground">
                      Target {ch.action_target} · Action {ch.action_type} · Ends {new Date(ch.ends_at).toLocaleDateString("en-GB")}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
